import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { ChevronRight } from "lucide-react";
import GeoMap from "bundles/common/components/GeoMap";

/**
 * CVthèque map on top of the shared GeoMap: each candidate is pinned where they
 * live and at every town they want to work in.
 */
const CandidatesMap = ({ candidates = [], loading = false, onSelectCandidate }) => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const points = useMemo(() => candidates.flatMap((candidate) => {
    const places = [candidate.home, ...(candidate.desired || [])].filter(Boolean);
    return places.map((point) => ({ id: candidate.id, lat: point.lat, lng: point.lng, data: candidate }));
  }), [candidates]);

  const getLabel = useCallback((items) => intl.formatMessage(
    { id: "recruiter.cv_theque.map_count", defaultMessage: "{count, plural, one {# candidate} other {# candidates}}" },
    { count: items.length }
  ), [intl]);

  const renderItems = useCallback(({ items, close }) => items.map(({ data: candidate }) => {
    const reasons = candidate.match_reasons || [];
    const wantsHere = !reasons.includes("lives_here") && reasons.includes("wants_here");
    const towns = (candidate.match_desired_labels || []).join(", ");
    const select = () => {
      close();
      onSelectCandidate?.(candidate.id);
    };
    return (
      <Box
        key={candidate.id}
        role="button"
        tabIndex={0}
        onClick={select}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          select();
        }}
        sx={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "8px 10px", borderRadius: "10px", cursor: "pointer", outline: "none",
          transition: "background-color 0.15s ease",
          "& .candidate-name": { transition: "color 0.15s ease" },
          "& .candidate-chevron": {
            flexShrink: 0, display: "flex", color: "hsl(153, 68%, 25%)",
            opacity: 0, transform: "translateX(-4px)", transition: "opacity 0.15s ease, transform 0.15s ease",
          },
          "&:hover, &:focus-visible": {
            bgcolor: "hsla(153, 68%, 25%, 0.06)",
            "& .candidate-name": { color: "hsl(153, 68%, 25%)" },
            "& .candidate-chevron": { opacity: 1, transform: "translateX(0)" },
          },
          "&:focus-visible": { boxShadow: "inset 0 0 0 2px hsla(153, 68%, 25%, 0.3)" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography className="candidate-name" sx={{
            fontSize: "13px", color: "hsl(178, 93%, 10%)", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
            whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
          }}>
            {candidate.name}
          </Typography>
          {candidate.job_title && (
            <Typography sx={{
              fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
            }}>
              {candidate.job_title}
            </Typography>
          )}
          {(candidate.location_label || wantsHere) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 6px" }}>
              {candidate.location_label && (
                <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {candidate.location_label}
                </Typography>
              )}
              {wantsHere && (
                <Box
                  component="span"
                  title={towns}
                  sx={{
                    fontSize: "11px", px: "8px", py: "2px", borderRadius: "999px",
                    bgcolor: "hsla(212, 72%, 42%, 0.08)", color: "hsl(212, 72%, 42%)",
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui", whiteSpace: "nowrap",
                  }}
                >
                  {intl.formatMessage({ id: "recruiter.cv_theque.match.wants_here", defaultMessage: "Wants to work here" })}
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box className="candidate-chevron">
          <ChevronRight size={16} />
        </Box>
      </Box>
    );
  }), [intl, onSelectCandidate]);

  return (
    <GeoMap
      points={points}
      getLabel={getLabel}
      labelLimit={Infinity}
      renderItems={renderItems}
      loading={loading}
      loadingMessage={intl.formatMessage({ id: "loading", defaultMessage: "Loading" })}
      fullscreenLabel={intl.formatMessage({ id: "map_fullscreen", defaultMessage: "Full screen" })}
      exitFullscreenLabel={intl.formatMessage({ id: "map_exit_fullscreen", defaultMessage: "Exit full screen" })}
      language={i18nLocale}
      emptyMessage={intl.formatMessage({ id: "recruiter.cv_theque.map_empty", defaultMessage: "No candidate to show on the map" })}
      errorMessage={intl.formatMessage({ id: "recruiter.cv_theque.map_unavailable", defaultMessage: "Map unavailable" })}
    />
  );
};

export default CandidatesMap;
